import React, { useMemo } from 'react';
import { Grid, DialogContent, makeStyles, Typography } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Skeleton } from '@mui/material';
import { useFormikContext } from 'formik';
import { StackedListMessageSummaryAlert } from '~/modules/common/components';
import { isoStringToFormattedISOString } from '~/modules/common/dates/convert';
import EstimateItemMetric from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/components/TaskEstimateCard/components/EstimateItemMetric';
import TaskAllocationCard from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/components/TaskAllocationCard';
import {
  ChangeTaskResourceConfirmationArea,
  ResourceUserSelectionRow
} from './components';
import { useOnChangeHandler } from './hooks';

const dateFormatString = 'LLL dd, yyyy';

export const getMessageSummary = ({ hasError, resourceName }) => ({
  title: {
    messageId:
      'changeTaskResourceDialog.noViewResourceAllocationPermissionError.title'
  },
  messages: hasError
    ? [
        {
          messageId:
            'changeTaskResourceDialog.noViewResourceAllocationPermissionError.noPermissionMessage'
        },
        {
          messageId:
            'changeTaskResourceDialog.noViewResourceAllocationPermissionError.allocationRestrictionMessage',
          values: { resourceName }
        }
      ]
    : []
});

const useStyles = makeStyles(theme => ({
  metricContainer: {
    paddingBottom: `${theme.spacing(2)}px !important`
  },
  root: {
    overflow: 'hidden',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  role: {
    paddingTop: '0px !important'
  },
  roleLabel: {
    color: theme.palette.text.secondary
  },
  confirmationArea: {
    marginTop: theme.spacing(0),
    padding: theme.spacing(0, 1)
  },
  infoArea: {
    marginBottom: theme.spacing(0)
  }
}));

const useEstimateMetricStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1)
  }
}));

const useFormattedHoursStyles = makeStyles(theme => ({
  noValue: {
    marginBottom: 0
  }
}));

export const useAllocationCardStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    maxWidth: theme.spacing(33.25)
  }
}));

export const ChangeTaskResourceDialogContent = ({
  isNewUserInfoLoading,
  resourceEstimate,
  setIsNewUserInfoLoading,
  task,
  resourceAllocationActions,
  dialogContentId
}) => {
  const classes = useStyles();
  const estimateMetricClasses = useEstimateMetricStyles();
  const allocationCardClasses = useAllocationCardStyles();
  const formattedHoursClasses = useFormattedHoursStyles();

  const {
    values: { availableHours, newResourceUser }
  } = useFormikContext();

  const {
    projectRole,
    taskResourceUserAllocation,
    resourceUser,
    taskResourceUserCostProgressSummary,
    taskResourceUserHoursProgressSummary
  } = resourceEstimate;

  const { startDate, endDate } = taskResourceUserAllocation || {};

  const taskAllocationDateRange = useMemo(
    () => ({
      startDate: isoStringToFormattedISOString(startDate, dateFormatString),
      endDate: isoStringToFormattedISOString(endDate, dateFormatString)
    }),
    [endDate, startDate]
  );

  const {
    canViewResourceAllocation,
    canEditResourceAllocation
  } = resourceAllocationActions;

  const { onResourceUserChange } = useOnChangeHandler({
    projectId: task.project.id,
    taskResourceUserAllocation,
    setIsNewUserInfoLoading,
    canEditResourceAllocation
  });

  const { displayText: projectRoleDisplayText } = projectRole || {};
  const {
    id: taskId,
    project: { id: projectId }
  } = task;

  const {
    title: permissionErrorTitle,
    messages: permissionErrorMessages
  } = getMessageSummary({
    hasError: !canViewResourceAllocation && Boolean(taskResourceUserAllocation),
    resourceName: resourceUser.displayText
  });

  return (
    <DialogContent className={classes.root} id={dialogContentId}>
      <Grid container className={classes.infoArea}>
        <Grid item className={classes.role}>
          <Typography>
            <span className={classes.roleLabel}>
              <FormattedMessage id="changeTaskResourceDialog.role" />
            </span>
            <span
              className={classNames({
                [classes.roleLabel]: !projectRoleDisplayText
              })}
            >
              {projectRoleDisplayText || (
                <FormattedMessage id="changeTaskResourceDialog.noRole" />
              )}
            </span>
          </Typography>
        </Grid>
        <Grid container item spacing={2} className={classes.metricContainer}>
          <Grid item md={canViewResourceAllocation ? 6 : 12}>
            <EstimateItemMetric
              formatMessageKey="changeTaskResourceDialog.estimate"
              cost={
                taskResourceUserCostProgressSummary?.estimatedAtCompletionCost
              }
              hours={
                taskResourceUserHoursProgressSummary?.estimatedAtCompletionHours
              }
              classes={estimateMetricClasses}
              formattedHoursClasses={formattedHoursClasses}
            />
          </Grid>
          {canViewResourceAllocation && (
            <Grid item md={6}>
              <TaskAllocationCard
                isReadOnlyView
                classes={allocationCardClasses}
              />
            </Grid>
          )}
        </Grid>
        <ResourceUserSelectionRow
          isDropdownDisabled={
            !canViewResourceAllocation && Boolean(taskResourceUserAllocation)
          }
          enableAllResourcesTab={canEditResourceAllocation}
          existingResourceUser={resourceUser}
          hideAvailability={Boolean(!taskResourceUserAllocation)}
          initialEstimatedHours={taskResourceUserAllocation?.totalHours}
          onResourceUserChange={onResourceUserChange}
          newResourceUser={newResourceUser}
          taskId={taskId}
          projectId={projectId}
          role={projectRole}
          startDate={startDate}
          endDate={endDate}
        />
      </Grid>
      <Grid item container className={classes.confirmationArea}>
        {isNewUserInfoLoading ? (
          <Skeleton height={110} width="100%" />
        ) : (
          newResourceUser && (
            <ChangeTaskResourceConfirmationArea
              canEditResourceAllocation={canEditResourceAllocation}
              existingResource={resourceUser}
              selectedResource={newResourceUser}
              taskResourceUserAllocation={taskResourceUserAllocation}
              taskAllocationDateRange={taskAllocationDateRange}
              availableHours={availableHours}
            />
          )
        )}
      </Grid>
      <StackedListMessageSummaryAlert
        severity="error"
        title={permissionErrorTitle}
        messages={permissionErrorMessages}
      />
    </DialogContent>
  );
};

ChangeTaskResourceDialogContent.propTypes = {
  dialogContentId: PropTypes.string.isRequired,
  resourceEstimate: PropTypes.object.isRequired,
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    project: PropTypes.shape({
      id: PropTypes.string.isRequired,
      resourceAllocationActions: PropTypes.shape({
        canEditResourceAllocation: PropTypes.bool.isRequired,
        canViewResourceAllocation: PropTypes.bool.isRequired
      })
    }).isRequired
  }).isRequired,
  isNewUserInfoLoading: PropTypes.bool,
  setIsNewUserInfoLoading: PropTypes.func,
  resourceAllocationActions: PropTypes.shape({
    canViewResourceAllocation: PropTypes.bool,
    canEditResourceAllocation: PropTypes.bool
  }).isRequired
};

export default ChangeTaskResourceDialogContent;
